import {startOfWeek, endOfWeek, addWeeks, subWeeks, getISOWeek, eachDayOfInterval, isSameDay, getYear} from "date-fns";
import formatDate from "../../helpers/formatDate";

const toValidDate = (value: string | Date): Date | null => {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** Get start of week (Monday) for a given date */
export function getWeekStart(date: Date): Date {
    return startOfWeek(date, {weekStartsOn: 1});
}

/** Get end of week (Sunday) for a given date */
export function getWeekEnd(date: Date): Date {
    return endOfWeek(date, {weekStartsOn: 1});
}

/** Get array of 7 days (Mon-Sun) for the week containing weekStart */
export function getWeekDays(weekStart: Date): Date[] {
    return eachDayOfInterval({
        start: weekStart,
        end: endOfWeek(weekStart, {weekStartsOn: 1}),
    });
}

/** Get week label like "KW 7, 2026" */
export function getWeekLabel(weekStart: Date): string {
    return "KW " + getISOWeek(weekStart) + ", " + getYear(weekStart);
}

/** Navigate to next week */
export function getNextWeekStart(weekStart: Date): Date {
    return addWeeks(weekStart, 1);
}

/** Navigate to previous week */
export function getPrevWeekStart(weekStart: Date): Date {
    return subWeeks(weekStart, 1);
}

/**
 * Get short day header like "Mo 09"
 * EEEEEE = 2-letter day abbreviation in German (Mo, Di, Mi, Do, Fr, Sa, So)
 * dd = day of month with leading zero
 */
export function getDayHeader(date: Date): string {
    return formatDate(date, "EEEEEE dd");
}

/**
 * Filter lessons that fall on a specific day.
 * Accepts any object whose courseLesson has a startDateTime field.
 */
export function getLessonsForDay<T extends {courseLesson: {startDateTime: string | Date}}>(lessons: T[], day: Date): T[] {
    return lessons.filter((item) => {
        const startDate = toValidDate(item.courseLesson.startDateTime);
        return startDate ? isSameDay(startDate, day) : false;
    });
}

/** Format time range like "14:00 - 15:30" */
export function formatTimeRange(startDateTime: string | Date, endDateTime: string | Date): string {
    const startDate = toValidDate(startDateTime);
    const endDate = toValidDate(endDateTime);

    if (!startDate || !endDate) {
        return "--:-- - --:--";
    }

    return formatDate(startDate, "HH:mm") + " - " + formatDate(endDate, "HH:mm");
}

/** Format date for cards like "Di, 11. Feb." */
export function formatShortDate(date: Date): string {
    return formatDate(date, "EEEEEE, d. MMM.");
}

/** Format day number like "04", "25" */
export function formatDayNumber(date: Date): string {
    return formatDate(date, "dd");
}

/** Format short weekday like "Mo", "Di", "Mi" */
export function formatWeekdayShort(date: Date): string {
    return formatDate(date, "EEEEEE");
}

/** Format date for group headers like "24. Februar 2026" */
export function formatGroupHeader(date: Date): string {
    return formatDate(date, "d. MMMM yyyy");
}

/** Format date for card display like "Mo 24. Feb" */
// export function formatCardDate(date: Date): string {
//     return formatDate(date, "EEEEEE  d.  MMM").replace(/\.$/, "");
// }
export function formatCardDate(date: Date): string {
    return formatDate(date, "EEEEEE\u00A0\u00A0\u00A0d.\u00A0\u00A0\u00A0MMM").replace(/\.$/, "");
}

/**
 * Group lessons by calendar day, sorted by date.
 * Lessons on the same day are grouped together.
 */
export function groupLessonsByDay<T extends {courseLesson: {startDateTime: string | Date}}>(lessons: T[]): Array<{date: Date; lessons: T[]}> {
    const groups: Array<{date: Date; lessons: T[]}> = [];
    for (const item of lessons) {
        const d = toValidDate(item.courseLesson.startDateTime);
        if (!d) {
            continue;
        }
        const existing = groups.find((g) => isSameDay(g.date, d));
        if (existing) {
            existing.lessons.push(item);
        } else {
            groups.push({date: d, lessons: [item]});
        }
    }
    return groups;
}

/** Format month header like "Februar 2026" */
export function formatMonthHeader(date: Date): string {
    return formatDate(date, "MMMM yyyy");
}

/**
 * Group lessons by month, then by day within each month.
 * Returns an array of month groups, each containing day groups.
 */
export function groupLessonsByMonth<T extends {courseLesson: {startDateTime: string | Date}}>(lessons: T[]): Array<{monthLabel: string; dayGroups: Array<{date: Date; lessons: T[]}>}> {
    const dayGroups = groupLessonsByDay(lessons);
    const months: Array<{monthLabel: string; dayGroups: Array<{date: Date; lessons: T[]}>}> = [];

    for (const dayGroup of dayGroups) {
        const label = formatMonthHeader(dayGroup.date);
        const existing = months.find((m) => m.monthLabel === label);
        if (existing) {
            existing.dayGroups.push(dayGroup);
        } else {
            months.push({monthLabel: label, dayGroups: [dayGroup]});
        }
    }
    return months;
}
